import { differenceInDays } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Link, Stack, Text } from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { Route } from '../../admin-navbar/config/router';

interface TrialCardProps {
	isExpanded: boolean;
}

export const TrialCard: FC<TrialCardProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const recognizeURL = useBuildRecognizeURL();

	const hasTrialEndDate = !!company.trial_end_date && company.status === 'in_trial';

	// Only show to admins when expanded, in trial with valid end date, and not superadmin-initialized
	if (!isExpanded || !user?.admin || !hasTrialEndDate || company.is_superadmin_initialized) return null;

	const daysRemaining = differenceInDays(new Date(company.trial_end_date), new Date());

	return (
		<Stack
			bgColor="bg.surface"
			borderRadius="md"
			border="1px solid"
			borderColor="border.default"
			p={4}
			my={2}
			spacing={2}
			overflow="hidden"
			opacity={isExpanded ? 1 : 0}
			transition="opacity 0.15s cubic-bezier(.4, 0, .2, 1)"
		>
			<Text fontSize={14} fontWeight="semibold" color="text.default" whiteSpace="nowrap">
				{t('trial_card.ends_in', { count: daysRemaining })}
			</Text>
			<Text fontSize={14} color="text.softer" whiteSpace="nowrap">
				{t('trial_card.upgrade_cta')}
			</Text>
			<Button as={Link} href={recognizeURL(Route.AdminBilling)} variant="solid" colorScheme="secondary" whiteSpace="nowrap">
				{t('trial_card.upgrade_button')}
			</Button>
		</Stack>
	);
};
